import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useAppStore } from '@/store'
import type { T_VisitorMessage, T_VisitorUnreadMessage } from './types'

type T_MessengerOpenPayload = {
  topicId?: number | null
  messageId?: number | null
}

interface T_MessagesStore {
  isUnreadInitialized: boolean
  unreadMessages: T_VisitorUnreadMessage[]
  topicActivityMap: Record<number, number>

  isMessengerOpen: boolean
  activeTopicId: number | null
  activeMessageId: number | null

  setUnreadMessages: (messages: T_VisitorUnreadMessage[]) => void
  addUnreadMessage: (message: T_VisitorUnreadMessage) => void
  markMessagesAsRead: (messageIds: number[]) => void
  getUnreadMessage: (messageId: number) => T_VisitorUnreadMessage | undefined
  listUnreadMessagesForTopic: (topicId?: number | null, topicSubject?: string) => T_VisitorUnreadMessage[]
  listDashboardMessages: (limit?: number) => T_VisitorUnreadMessage[]
  isMessageMine: (userId: number | null | undefined, message: T_VisitorMessage | T_VisitorUnreadMessage) => boolean
  updateTopicActivity: (topicId: number, timestamp: Date | number) => void

  openMessenger: (payload?: T_MessengerOpenPayload) => void
  closeMessenger: () => void
  reset: () => void
}

const sortByTimestampDesc = (messages: T_VisitorUnreadMessage[]) => {
  return [ ...messages ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
}

const normalizeMessageIds = (messageIds: number[]) => {
  return messageIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
}

const isOwnUnreadMessage = (message: T_VisitorUnreadMessage) => {
  const currentUserId = useAppStore.getState().userData.id
  if (!currentUserId) return false
  return String(currentUserId) === String(message.author.id)
}

export const useMessagesStore = create<T_MessagesStore>()(
  devtools(
    (set, get) => ({
      isUnreadInitialized: false,
      unreadMessages: [],
      topicActivityMap: {},

      isMessengerOpen: false,
      activeTopicId: null,
      activeMessageId: null,

      setUnreadMessages: (messages) => set(
        (state) => {
          const uniq = new Map<number, T_VisitorUnreadMessage>()
          messages.forEach((item) => {
            if (item.id > 0 && !isOwnUnreadMessage(item)) {
              uniq.set(item.id, item)
            }
          })

          const nextMessages = sortByTimestampDesc(Array.from(uniq.values()))
          const nextTopicActivityMap = { ...state.topicActivityMap }
          nextMessages.forEach((item) => {
            nextTopicActivityMap[item.topic.id] = Math.max(
              nextTopicActivityMap[item.topic.id] || 0,
              item.timestamp.getTime(),
            )
          })

          return {
            isUnreadInitialized: true,
            unreadMessages: nextMessages,
            topicActivityMap: nextTopicActivityMap,
          }
        },
        false,
        'setUnreadMessages',
      ),

      addUnreadMessage: (message) => set(
        (state) => {
          if (!message?.id) return state
          if (isOwnUnreadMessage(message)) return state

          const hasMessage = state.unreadMessages.some((item) => item.id === message.id)
          const unreadMessages = hasMessage
            ? state.unreadMessages.map((item) => item.id === message.id ? message : item)
            : [ message, ...state.unreadMessages ]

          const topicId = Number(message?.topic?.id) || 0
          const topicActivityMap = topicId > 0
            ? {
              ...state.topicActivityMap,
              [topicId]: Math.max(
                state.topicActivityMap[topicId] || 0,
                message.timestamp.getTime(),
              ),
            }
            : state.topicActivityMap

          return {
            unreadMessages: sortByTimestampDesc(unreadMessages),
            topicActivityMap,
          }
        },
        false,
        'addUnreadMessage',
      ),

      markMessagesAsRead: (messageIds) => set(
        (state) => {
          const normalizedIds = new Set<number>(normalizeMessageIds(messageIds))
          if (!normalizedIds.size) return state

          return {
            unreadMessages: state.unreadMessages.filter((item) => !normalizedIds.has(item.id)),
          }
        },
        false,
        'markMessagesAsRead',
      ),

      getUnreadMessage: (messageId) => {
        const id = Number(messageId)
        if (!Number.isFinite(id) || id <= 0) return undefined
        return get().unreadMessages.find((item) => item.id === id)
      },

      listUnreadMessagesForTopic: (topicId, topicSubject) => {
        const messages = get().unreadMessages
        return messages.filter((message) => {
          if (topicId != null && String(message.topic?.id) === String(topicId)) {
            return true
          }

          if (topicSubject && message.topic?.subject === topicSubject) {
            return true
          }

          return false
        })
      },

      listDashboardMessages: (limit) => {
        const filtered = get()
          .unreadMessages
          .filter((item) => item.isDashboardShow)

        const sorted = sortByTimestampDesc(filtered)
        if (!Number.isFinite(limit) || (limit || 0) <= 0) return sorted
        return sorted.slice(0, limit)
      },

      isMessageMine: (userId, message) => {
        if (!userId) return false
        return String(userId) === String(message.author.id)
      },

      updateTopicActivity: (topicId, timestamp) => set(
        (state) => {
          const id = Number(topicId)
          if (!Number.isFinite(id) || id <= 0) return state

          const ts = timestamp instanceof Date
            ? timestamp.getTime()
            : Number(timestamp)

          if (!Number.isFinite(ts) || ts <= 0) return state

          return {
            topicActivityMap: {
              ...state.topicActivityMap,
              [id]: Math.max(state.topicActivityMap[id] || 0, ts),
            },
          }
        },
        false,
        'updateTopicActivity',
      ),

      openMessenger: (payload) => set(
        () => ({
          isMessengerOpen: true,
          activeTopicId: payload?.topicId ?? null,
          activeMessageId: payload?.messageId ?? null,
        }),
        false,
        'openMessenger',
      ),

      closeMessenger: () => set(
        () => ({
          isMessengerOpen: false,
          activeTopicId: null,
          activeMessageId: null,
        }),
        false,
        'closeMessenger',
      ),

      reset: () => set(
        () => ({
          isUnreadInitialized: false,
          unreadMessages: [],
          topicActivityMap: {},
          isMessengerOpen: false,
          activeTopicId: null,
          activeMessageId: null,
        }),
        false,
        'reset',
      ),
    }),
    { store: 'Messages' },
  ),
)
